import { FormikProps } from "formik";
import { Fragment, useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import Flex from "@/components/Flex";
import {
  ExpandCollapseAllButton,
  HeaderActionPortal,
  SectionGroupHeading,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import { EncounterSumaryFormTypeEnum } from "@/services";
import { NOTE_REQUIRED_SUMMARY_ID } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/contant/encounter-constant";
import { Form2Payload } from "@/pages/app/provider/scheduling/appointment/encounter/encounter-constant";
import InitialF2fSectionRenderer from "./components/InitialF2fSectionRenderer";
import RequiredSectionFrame from "./components/RequiredSectionFrame";
import RequiredSectionsSummary from "./components/RequiredSectionsSummary";
import {
  REQUIRED_SECTION_ELEMENT_ID_PREFIX,
  REQUIRED_SECTIONS_ATTEMPT_STATUS,
  REQUIRED_SECTIONS_MISSING_STATUS,
} from "./initial-f2f-required-constant";
import { useMissingSectionFocus } from "./hooks/use-missing-section-focus";
import { isRequiredSectionKey } from "./initial-f2f-required-utils";
import { useInitialF2fNote } from "./hooks/use-initial-f2f-note";
import { useCodeFieldsSync } from "./hooks/use-code-fields-sync";
import { useNoteContentSync } from "./hooks/use-note-content-sync";
import { INITIAL_F2F_SECTIONS } from "./initial-f2f-constant";
import {
  INITIAL_F2F_DATE_LABEL_OVERRIDES,
  INITIAL_F2F_HIDDEN_SECTION_KEYS,
  INITIAL_F2F_SECTION_TITLE_OVERRIDES,
} from "./initial-f2f-form-options-constant";
import { filterVisibleSections } from "./initial-f2f-utils";
import { InitialF2fPayload } from "./initial-f2f-types";

export interface InitialF2fNoteProps {
  formik: FormikProps<Form2Payload>;
  formType?: EncounterSumaryFormTypeEnum;
  initialValue?: InitialF2fPayload;
}

const InitialF2fNote = ({
  formik,
  formType,
  initialValue,
}: InitialF2fNoteProps) => {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get("appointmentid") ?? undefined;
  const visibleSections = useMemo(
    () =>
      filterVisibleSections(
        INITIAL_F2F_SECTIONS,
        formType && INITIAL_F2F_HIDDEN_SECTION_KEYS[formType],
        formType && INITIAL_F2F_SECTION_TITLE_OVERRIDES[formType]
      ),
    [formType]
  );
  const sectionKeys = useMemo(
    () =>
      visibleSections.flatMap((group) =>
        group.sections.map((section) => section.key)
      ),
    [visibleSections]
  );
  const {
    payload,
    update,
    updateKeyedField,
    isExpanded,
    toggleSection,
    isAllExpanded,
    toggleAllSections,
  } = useInitialF2fNote(initialValue, sectionKeys);

  const dateLabels = formType
    ? INITIAL_F2F_DATE_LABEL_OVERRIDES[formType]
    : undefined;

  useNoteContentSync({ formik, payload, update, dateLabels });
  useCodeFieldsSync({ formik, payload });

  // Snapshot taken by Save & Sign; the form is not re-validated while the user edits it.
  const status = formik.status as Record<string, unknown> | undefined;
  const missingKeys = (status?.[REQUIRED_SECTIONS_MISSING_STATUS] ??
    []) as string[];
  const signAttempt = Number(status?.[REQUIRED_SECTIONS_ATTEMPT_STATUS]) || 0;
  const missingSections = useMemo(
    () =>
      visibleSections
        .flatMap((group) => group.sections)
        .filter((section) => missingKeys.includes(section.key))
        .map((section) => ({
          key: section.key,
          title: section.title,
          isRequired: true,
          isAnswered: false,
        })),
    [visibleSections, missingKeys]
  );

  const goToSection = useCallback(
    (key: string) => {
      if (!isExpanded(key)) toggleSection(key);
      // The note scrolls inside its own panel, so the element scrolls itself into view there.
      document
        .getElementById(`${REQUIRED_SECTION_ELEMENT_ID_PREFIX}${key}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [isExpanded, toggleSection]
  );
  useMissingSectionFocus({
    attempt: signAttempt,
    firstMissingKey: missingSections[0]?.key,
    goToSection,
  });

  return (
    <>
      <HeaderActionPortal targetId={NOTE_REQUIRED_SUMMARY_ID}>
        <RequiredSectionsSummary
          missing={missingSections}
          onSelectSection={goToSection}
        />
      </HeaderActionPortal>
      <Flex
        flexDirection="column"
        gap={2}
        p={1.5}
        sx={{ width: "100%", maxWidth: "100%" }}
      >
        <HeaderActionPortal>
          <ExpandCollapseAllButton
            isAllExpanded={isAllExpanded}
            onToggle={toggleAllSections}
          />
        </HeaderActionPortal>
        {visibleSections.map((group) => (
          <Fragment key={group.heading}>
            <SectionGroupHeading>{group.heading}</SectionGroupHeading>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {group.sections.map((section) => (
                <RequiredSectionFrame
                  key={section.key}
                  sectionKey={section.key}
                  isRequired={isRequiredSectionKey(section.key)}
                  isMissing={missingKeys.includes(section.key)}
                >
                  <InitialF2fSectionRenderer
                    section={section}
                    expanded={isExpanded(section.key)}
                    onToggle={() => toggleSection(section.key)}
                    payload={payload}
                    update={update}
                    updateKeyedField={updateKeyedField}
                    dateLabels={dateLabels}
                    chiefComplaint={formik.values.chiefComplaint ?? ""}
                    onChiefComplaintChange={(value) =>
                      formik.setFieldValue("chiefComplaint", value)
                    }
                    appointmentId={appointmentId}
                    clockDrawingUrl={formik.values.clockDrawingUrl}
                  />
                </RequiredSectionFrame>
              ))}
            </Box>
          </Fragment>
        ))}
      </Flex>
    </>
  );
};

export default InitialF2fNote;
